import { useCallback } from 'react';

import type { FilterFormValues } from 'modules/Teacher/components/TeacherSearchFilters';

// Bumped to v4 to drop stale session filters that emptied the feed
// (e.g. profile subject "подготовка к ОГЭ" hiding "геометрия" заявки).
const STORAGE_KEY = 'teacher-bulletin-filters-v4';

export const DEFAULT_BULLETIN_FILTERS: FilterFormValues = {
  priceFrom: '',
  priceTo: '',
  subjectSubcategories: [],
  learnerAudience: [],
  grade: [],
  studyLocations: [],
};

export const countActiveBulletinFilters = (values: FilterFormValues): number => {
  let count = 0;
  if (values.priceFrom?.trim()) count += 1;
  if (values.priceTo?.trim()) count += 1;
  count += values.subjectSubcategories.length;
  count += values.learnerAudience.length;
  count += values.grade.length;
  count += values.studyLocations.length;
  return count;
};

export const hasStoredBulletinFilters = (): boolean => {
  try {
    return sessionStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
};

export const useBulletinSearchFilters = () => {
  const load = useCallback((): FilterFormValues => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return { ...DEFAULT_BULLETIN_FILTERS };
      }
      const parsed = JSON.parse(raw) as Partial<FilterFormValues> & {
        subjectCategory?: string;
        subjectSubcategory?: string;
        tutorGenderPreference?: string;
        learnerAudience?: string | string[];
      };
      const legacySubcategories = [
        ...(parsed.subjectSubcategories ?? []),
        ...(parsed.subjectSubcategory ? [parsed.subjectSubcategory] : []),
      ].filter(Boolean);
      const legacyAudience = parsed.learnerAudience;
      const learnerAudience = Array.isArray(legacyAudience)
        ? legacyAudience.filter((v) => v === 'school' || v === 'graduated')
        : legacyAudience && legacyAudience !== 'any'
          ? [legacyAudience]
          : [];
      return {
        ...DEFAULT_BULLETIN_FILTERS,
        ...parsed,
        subjectSubcategories: Array.isArray(parsed.subjectSubcategories)
          ? parsed.subjectSubcategories
          : legacySubcategories,
        learnerAudience,
        grade: Array.isArray(parsed.grade) ? parsed.grade : [],
        studyLocations: Array.isArray(parsed.studyLocations) ? parsed.studyLocations : [],
      };
    } catch {
      return { ...DEFAULT_BULLETIN_FILTERS };
    }
  }, []);

  const save = useCallback((values: FilterFormValues) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(values));
    } catch {
      // ignore quota / private mode errors
    }
  }, []);

  const clear = useCallback(() => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  return { load, save, clear };
};

type TutorProfileForFilters = {
  teacher_prices?: Array<{ subcategory_name?: string; service?: string }>;
  format_online?: boolean;
  format_offline?: boolean;
  grades?: number[];
};

export const buildDefaultFiltersFromProfile = (
  profile: TutorProfileForFilters,
): FilterFormValues => {
  const subcategories = new Set<string>();
  for (const price of profile.teacher_prices ?? []) {
    const sub = price.subcategory_name?.trim();
    if (sub) subcategories.add(sub);
  }
  const grades = (profile.grades ?? []).map((g) => String(g));
  const studyLocations: string[] = [];
  if (profile.format_online) studyLocations.push('online');
  if (profile.format_offline) {
    studyLocations.push('at_tutor', 'at_student');
  }
  return {
    ...DEFAULT_BULLETIN_FILTERS,
    subjectSubcategories: Array.from(subcategories),
    grade: grades,
    studyLocations,
  };
};
